import { useMemo } from "react";
import {
  Bar, BarChart, CartesianGrid, Legend, Tooltip, XAxis, YAxis,
} from "recharts";
import { CockpitPanel, useCockpitAdmin } from "@/cockpit/CockpitPanel";
import { FixedChart } from "@/cockpit/FixedChart";
import { FlowSplitBar, MagnitudeBar } from "@/cockpit/FlowSplitBar";
import { HatchDefs } from "@/cockpit/chartMarks";
import { AXIS, GRID, INFLOW, compactShares, compactUsd, tooltipBox } from "@/cockpit/formatCockpit";

const FLOW_H = 220;

function InflowOutflowLegend({ payload }) {
  return (
    <div className="flex flex-wrap gap-3 justify-center text-[10px] pt-1" style={{ color: "var(--hoip-text-tertiary)" }}>
      {(payload || []).map((p) => (
        <span key={p.dataKey} className="inline-flex items-center gap-1" style={{ color: p.color }}>
          <span aria-hidden="true">{p.dataKey === "inflowUsd" ? "▲" : "▼"}</span>
          {p.value}
        </span>
      ))}
    </div>
  );
}

export function InstitutionalFlowChart({ flows, holders, note, onOpenRaw }) {
  const isAdmin = useCockpitAdmin();
  const data = useMemo(
    () => (flows || []).map((r) => ({
      quarter: r.quarter,
      inflowUsd: r.inflowUsd,
      outflowUsd: r.outflowUsd,
      netShares: r.netShares,
    })),
    [flows],
  );
  const hasFlow = data.length > 0;
  const period = holders?.[0]?.periodOfReport;

  return (
    <CockpitPanel
      title="Institutional context · 13F"
      hint={hasFlow
        ? "Lagged holdings snapshot. Bars are Q/Q gross flow, not a live tape."
        : "13F is a lagged holdings snapshot (~45 days), not a buy/sell tape. Bars are this ticker's reported position value at each filer."}
      onOpenRaw={onOpenRaw}
      testid="institutional-flow-chart"
    >
      {hasFlow ? (
        <FixedChart height={FLOW_H}>
          {(width) => (
            <BarChart width={width} height={FLOW_H} data={data} barGap={2} onClick={onOpenRaw}>
              <HatchDefs />
              <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="quarter" tick={{ fill: AXIS, fontSize: 9 }} interval={0} angle={-25} dy={8} height={44} />
              <YAxis tick={{ fill: AXIS, fontSize: 9 }} tickFormatter={compactUsd} width={52} />
              <Tooltip
                contentStyle={tooltipBox}
                formatter={(v, name) => [
                  compactUsd(v),
                  name === "inflowUsd" ? "▲ Gross inflow" : "▼ Gross outflow",
                ]}
              />
              <Legend content={<InflowOutflowLegend />} />
              <Bar dataKey="inflowUsd" name="Gross inflow" fill="url(#cockpit-inflow-hatch)" maxBarSize={18} isAnimationActive={false} />
              <Bar dataKey="outflowUsd" name="Gross outflow" fill="url(#cockpit-outflow-hatch)" maxBarSize={18} isAnimationActive={false} />
            </BarChart>
          )}
        </FixedChart>
      ) : (
        isAdmin && (period || note) ? (
          <p className="text-[10px] mb-2" style={{ color: "var(--hoip-text-tertiary)" }}>
            {period ? `Period of report ${period}. ` : ""}{note || ""}
          </p>
        ) : null
      )}
      <HolderValueBars holders={holders} />
      {!hasFlow && !(holders || []).length ? (
        <p className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>No 13F holders in this snapshot.</p>
      ) : null}
    </CockpitPanel>
  );
}

function HolderValueBars({ holders }) {
  const rows = holders || [];
  if (!rows.length) return null;
  const maxVal = Math.max(1, ...rows.map((h) => Number(h.positionValueUsd) || 0));
  const hasSplit = rows.some((h) => (h.newPositionUsd || 0) + (h.exitUsd || 0) > 0);
  return (
    <div className="space-y-1.5 min-w-0" data-testid="13f-holder-split">
      <div className="flex items-center text-[9px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>
        <span className="w-[38%]">Top holders</span>
        <span className="w-[22%] text-right">Pos. value (ticker)</span>
        <span className="flex-1 pl-2">{hasSplit ? "▲ New vs ▼ Exit" : "Position"}</span>
      </div>
      {rows.slice(0, 8).map((h) => (
        <div key={h.investorName} className="flex items-center gap-2 text-[10px]">
          <span className="w-[38%] truncate" title={h.investorName}>{h.investorName}</span>
          <span
            className="w-[22%] text-right font-mono-data"
            style={{ color: "var(--hoip-text-tertiary)" }}
          >
            {compactUsd(h.positionValueUsd)}
          </span>
          {hasSplit ? (
            <FlowSplitBar
              leftUsd={h.newPositionUsd}
              rightUsd={h.exitUsd}
              leftLabel="▲ New"
              rightLabel="▼ Exit"
            />
          ) : (
            <div className="flex-1 min-w-0">
              <MagnitudeBar value={h.positionValueUsd} max={maxVal} color={INFLOW} />
              <div className="text-[8px] mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>
                {Number(h.shares) ? `${compactShares(h.shares)} sh` : "—"}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
